/** Random-access read-only bytes. Implemented for Blob/File, HTTP Range and node fs. */
export interface ByteSource {
  readonly size: number;
  read(offset: number, length: number): Promise<Uint8Array>;
}

export class BlobByteSource implements ByteSource {
  readonly size: number;
  constructor(private readonly blob: Blob) {
    this.size = blob.size;
  }
  async read(offset: number, length: number): Promise<Uint8Array> {
    return new Uint8Array(await this.blob.slice(offset, offset + length).arrayBuffer());
  }
}

export class MemoryByteSource implements ByteSource {
  readonly size: number;
  constructor(private readonly bytes: Uint8Array) {
    this.size = bytes.length;
  }
  async read(offset: number, length: number): Promise<Uint8Array> {
    return this.bytes.subarray(offset, offset + length);
  }
}

/** Reads a hosted file with HTTP Range requests. `size` comes from a HEAD/Range probe. */
export class HttpByteSource implements ByteSource {
  private constructor(
    private readonly url: string,
    readonly size: number,
    private readonly init: RequestInit,
  ) {}

  static async open(url: string, init: RequestInit = {}): Promise<HttpByteSource> {
    const res = await fetch(url, { ...init, headers: { ...(init.headers ?? {}), Range: 'bytes=0-0' } });
    if (res.status !== 206) throw new Error(`${url}: range requests unsupported (HTTP ${res.status})`);
    const range = res.headers.get('Content-Range') ?? '';
    const total = Number(range.split('/')[1]);
    if (!Number.isFinite(total)) throw new Error(`${url}: bad Content-Range "${range}"`);
    await res.arrayBuffer();
    return new HttpByteSource(url, total, init);
  }

  async read(offset: number, length: number): Promise<Uint8Array> {
    if (length === 0) return new Uint8Array(0);
    const res = await fetch(this.url, {
      ...this.init,
      headers: { ...(this.init.headers ?? {}), Range: `bytes=${offset}-${offset + length - 1}` },
    });
    if (res.status !== 206 && res.status !== 200) throw new Error(`${this.url}: HTTP ${res.status}`);
    const buf = new Uint8Array(await res.arrayBuffer());
    // A server that ignored Range sent the whole file.
    return res.status === 200 ? buf.subarray(offset, offset + length) : buf;
  }
}
